<template>
  <section class="overflow-auto sm:overflow-visible">
    <table class="w-full text-left">
      <thead>
        <tr>
          <th
            v-for="(col, index) in columns"
            :key="index"
            class="px-4 py-4 font-semibold font-sans text-base cursor-pointer text-fg-link-primary"
            :class="col.classes"
            :id="`datatable-heading-${index}`"
            @click="sort(col.field)"
          >
            <span>{{ col.label }}</span>
            <template v-if="col.tooltip">
              <OnClickOutside
                @trigger="ui.showTooltip = false"
                class="absolute bottom-12 mt-4 w-64 text-center"
              >
                <SlotPanel
                  :id="`report-tooltip-panel-${index}`"
                  :class="ui.showTooltip ? 'block' : 'hidden'"
                >
                  <div class="font-sans font-normal text-sm">{{ col.tooltip }}</div>
                </SlotPanel>
              </OnClickOutside>
              <span
                :id="`report-tooltip-cta-${index}`"
                class="text-fg-link-primary text-xs cursor-pointer"
                @click="ui.showTooltip = !ui.showTooltip"
              >
                (?)
              </span>
            </template>
            <span
              v-show="sortOptions.field === col.field"
              class="sort-arrow"
              :class="sortOptions.direction === 'asc' ? 'sort-asc-arrow' : 'sort-desc-arrow'"
            >
              {{ sortOptions.direction === 'asc' ? '↑' : '↓' }}
            </span>
          </th>
        </tr>
      </thead>
      <tbody class="border-t border-linear-subtle">
        <tr
          v-for="(items, itemsKey) in itemsClean"
          :key="itemsKey"
          :id="`datatable-${itemsKey}`"
          class="datatable-row border-solid border-b border-linear-subtle last:border-b-0 hover:bg-surface-primary"
        >
          <td
            v-for="(item, itemKey) in items"
            :key="itemKey"
            class="px-4 py-2 font-sans text-sm"
            :class="item.classes"
            :id="`datatable-${itemsKey}-${item.field}-${itemKey}`"
          >
            {{ item.value }}
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>
<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import { PropType, defineComponent } from 'vue'

import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import { sortBy } from '@/utils/utils'

interface Columns {
  field: string
  label: string
  classes?: string
  tooltip?: string
  // eslint-disable-next-line no-unused-vars
  mask?(value: any): string
}

type SortDirection = 'asc' | 'desc'

export default defineComponent({
  name: 'DataTable',
  components: {
    OnClickOutside,
    SlotPanel,
  },
  data() {
    return {
      ui: {
        showTooltip: false,
      },
      sortOptions: {
        field: '',
        direction: 'asc' as SortDirection,
      },
    }
  },
  props: {
    columns: { type: Array as PropType<Columns[]>, required: true },
    items: { type: Array as PropType<any[]>, required: true },
  },
  methods: {
    sort(field: string) {
      if (this.sortOptions.field === field) {
        this.sortOptions.direction = this.sortOptions.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortOptions.field = field
        this.sortOptions.direction = 'asc'
      }
    },
  },
  computed: {
    itemsClean(): any[] {
      const cols = this.columns

      let sortedItems = [...this.items]
      if (this.sortParam) {
        sortedItems = sortBy(sortedItems, this.sortParam as any)
      }

      return sortedItems.map(item => {
        return cols.map(col => {
          return {
            field: col.field,
            value: col.mask ? col.mask(item[col.field]) : item[col.field],
            classes: col.classes,
            tooltip: col.tooltip,
          }
        })
      })
    },
    sortParam(): string {
      if (this.sortOptions.field) {
        return `${this.sortOptions.direction === 'desc' ? '-' : ''}${this.sortOptions.field}`
      }
      return ''
    },
  },
})
</script>
